import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Menu, X, LogOut, LayoutDashboard, ChevronDown, Code2, Factory, Bot } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const servicesRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) setServicesOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const links = [
    { to: "/products", label: "Products", isRoute: true },
    { to: "/blogs", label: "Blogs", isRoute: true },
    { to: "/contact", label: "Contact", isRoute: true },
  ];

  const servicesLinks = [
    { to: "/software", label: "Web Application Development", desc: "Enterprise, SaaS, dashboards & APIs", icon: Code2 },
    { to: "/software", label: "Mobile Application Development", desc: "Android, iOS, Flutter, React Native", icon: Code2 },
    { to: "/#ai", label: "AI Automation", desc: "Chatbots, voice agents, RAG, RPA", icon: Bot, isHash: true },
    { to: "/plant-engineering", label: "Plant Engineering Design", desc: "Piping, E3D, Plant 3D, mechanical, MTC", icon: Factory },
  ];

  const dashHref = user?.role === "admin" ? "/admin" : "/dashboard";

  return (
    <header className="fixed top-0 inset-x-0 z-40">
      <div className="glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <img
              src="/brand/zerolabs-mark.png"
              alt=""
              aria-hidden="true"
              className="h-10 w-auto select-none drop-shadow-[0_0_12px_rgba(59,130,246,0.45)] group-hover:drop-shadow-[0_0_18px_rgba(59,130,246,0.7)] transition-all"
              draggable="false"
            />
            <img
              src="/brand/zerolabs-wordmark.png"
              alt="ZeroLabs Digitals"
              className="h-8 sm:h-9 w-auto select-none drop-shadow-[0_0_10px_rgba(59,130,246,0.4)] group-hover:drop-shadow-[0_0_16px_rgba(59,130,246,0.65)] transition-all"
              draggable="false"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            <Link
              to="/"
              data-testid="nav-link-home"
              className="text-sm text-[#94A3B8] hover:text-white transition-colors font-medium"
            >
              Home
            </Link>

            {/* Services mega-menu */}
            <div ref={servicesRef} className="relative">
              <button
                data-testid="nav-services-toggle"
                onClick={() => setServicesOpen((s) => !s)}
                className="text-sm text-[#94A3B8] hover:text-white transition-colors font-medium inline-flex items-center gap-1"
                aria-haspopup="true"
                aria-expanded={servicesOpen}
              >
                Services <ChevronDown size={14} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`}/>
              </button>
              {servicesOpen && (
                <div
                  data-testid="nav-services-menu"
                  className="absolute top-full left-0 mt-3 w-[360px] border border-white/10 rounded-2xl p-2 shadow-2xl backdrop-blur-xl"
                  style={{ backgroundColor: "rgba(8, 6, 15, 0.97)" }}
                >
                  {servicesLinks.map(({ to, label, desc, icon: Icon, isHash }) => {
                    const inner = (
                      <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/[0.05] transition-colors">
                        <div className="w-9 h-9 rounded-lg bg-[#22D3EE]/10 text-[#22D3EE] flex items-center justify-center shrink-0"><Icon size={16}/></div>
                        <div>
                          <div className="text-white font-semibold text-sm">{label}</div>
                          <div className="text-xs text-[#94A3B8] mt-0.5">{desc}</div>
                        </div>
                      </div>
                    );
                    return isHash ? (
                      <a key={label} href={to} onClick={() => setServicesOpen(false)} data-testid={`nav-services-${label.toLowerCase().replace(/\s+/g,'-')}`}>{inner}</a>
                    ) : (
                      <Link key={label} to={to} onClick={() => setServicesOpen(false)} data-testid={`nav-services-${label.toLowerCase().replace(/\s+/g,'-')}`}>{inner}</Link>
                    );
                  })}
                </div>
              )}
            </div>

            {links.map((l) => {
              if (l.isRoute) {
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-[#94A3B8] hover:text-white transition-colors font-medium"
                  >
                    {l.label}
                  </Link>
                );
              }
              return (
                <a
                  key={l.to}
                  href={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm text-[#94A3B8] hover:text-white transition-colors font-medium"
                >
                  {l.label}
                </a>
              );
            })}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <button
                  data-testid="nav-dashboard-btn"
                  onClick={() => nav(dashHref)}
                  className="btn-secondary !py-2 !px-4 text-sm"
                >
                  <LayoutDashboard size={16} /> Dashboard
                </button>
                <button
                  data-testid="nav-logout-btn"
                  onClick={async () => { await logout(); nav("/"); }}
                  className="text-sm text-[#94A3B8] hover:text-white inline-flex items-center gap-1"
                >
                  <LogOut size={14} /> Sign out
                </button>
              </>
            ) : (
              <>
                <NavLink data-testid="nav-login-link" to="/login" className="text-sm text-[#94A3B8] hover:text-white">Sign in</NavLink>
                <a data-testid="nav-book-cta" href="/contact#book" className="btn-primary !py-2 !px-4 text-sm">Book a call</a>
              </>
            )}
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-white p-2"
            onClick={() => setOpen((s) => !s)}
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden glass border-b border-white/10">
          <div className="px-6 py-4 flex flex-col gap-3">
            <Link to="/" onClick={() => setOpen(false)} className="text-white py-1">Home</Link>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mt-2">Services</div>
            <Link to="/software" onClick={() => setOpen(false)} className="text-white py-1">Web Application Development</Link>
            <Link to="/software" onClick={() => setOpen(false)} className="text-white py-1">Mobile Application Development</Link>
            <a href="/#ai" onClick={() => setOpen(false)} className="text-white py-1">AI Automation</a>
            <Link to="/plant-engineering" onClick={() => setOpen(false)} className="text-white py-1">Plant Engineering Design</Link>
            <div className="border-t border-white/10 my-2"/>
            <Link to="/products" onClick={() => setOpen(false)} className="text-[#94A3B8] hover:text-white py-1">Products</Link>
            <Link to="/blogs" onClick={() => setOpen(false)} className="text-[#94A3B8] hover:text-white py-1">Blogs</Link>
            <Link to="/contact" onClick={() => setOpen(false)} className="text-[#94A3B8] hover:text-white py-1">Contact</Link>
            {user ? (
              <>
                <button onClick={() => { setOpen(false); nav(dashHref); }} className="text-left text-[#22D3EE] py-1">Dashboard</button>
                <button onClick={async () => { setOpen(false); await logout(); nav("/"); }} className="text-left text-[#94A3B8] py-1">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-[#22D3EE] py-1">Sign in</Link>
                <a href="/contact#book" onClick={() => setOpen(false)} className="btn-primary text-center !py-2">Book a call</a>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
